import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { Communicator } from '../../tools/Communication';
import { DateTimeHelpers } from '../../tools/DateTimeHelpers';
import {
  LoadingContainer,
  PageContainer,
  PageHeader,
} from '../../components/Layout';
import './Container.css';
import { getContainerQrCodeImgUrl } from '@/utils/qrCode';
import { ContainerLocationStatus } from '@/utils/containerLocation';
import { IdName } from '../../components/EditMerchantOrder/HandleStatusChange';
import i18n from '@/utils/i18n';
import { Link } from 'react-router-dom';
import { ContentSection } from '../../components/Layout/ContentSection';
import ContainerTypeDisplay from '../../components/ContainerType/ContainerTypeDisplay';
import { Routes } from '@/utils/routes';
import ContainerLocationItem from './ContainerLocationItem';
import { ContainerBrokenReports } from './ContainerBrokenReports';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export interface HistoryResponse {
  merchants: IMerchant[];
  locations: ILocation[];
  users: IUser[];
  container: IContainer;
  checkoutCount: number;
  internalTouchPointCount: number;
  daysInSystem: number;
  soldCount: number;
  deadlineExtensionsCount: number;
  containerStations: IdName[];
}

export interface IMerchant {
  id: string;
  name: string;
  managerIds: string[];
  stores: IMerchantStore[];
}

export interface IMerchantStore {
  id: string;
  name: string;
  managerIds: string[];
  appUserIds: string[];
  merchant: IMerchant;
}

export interface ILocation {
  id: number;
  ownerSince: string;
  ownerTill: string | null;
  returnDeadline: string | null;
  status: number;
  ownerId: string;
  owner: IUser | null;
  payment: IPayment | null;
  locationNumber: number;
  isOnHold: boolean;
  onHoldSince: string | null;
  isBillableTransaction: boolean | null;
  deliveryTokenId: string | null;
  isSelfScan: string | null;
  transactionType: string | null;
  containerStationId: string | null;
  statusHistory: Record<string, string>;
  lastStatusUpdateAt: string;
  returnOrders: ILocationReturnOrder[];
}

export interface ILocationReturnOrder {
  merchantOrderId: string;
  deliveryNoteId: number;
  source: 'scanned' | 'typed';
  addedAt: string;
  removedAt: string | null;
  removalReason:
    | 'checked_out'
    | 'transferred'
    | 'moved_to_other_return'
    | 'order_canceled'
    | null;
}

interface IPayment {
  containerLocationId: number;
  timestamp: string;
  price: number;
  currency: { name: string; symbol: string };
}

interface IUser {
  id: string;
  username: string;
  merchant: IMerchant | null;
  merchantStore: IMerchantStore | null;
}

export interface ContainerType {
  id: number;
  name: string;
  class: string;
  internalName: string;
  namesByLanguage: { [lang: string]: string };
  orderKey: number;
  iconUrl: string;
  sizeHelper: string;
  imageUrl: string;
  countryCodes: string[];
  hexCode: string;
  packingUnit: number;
  groupId: number;
  sku: string;
  branding: string;
  legalOwner: string;
  packingInstructions: string;
  cleanupNote: string;
  weight: number;
  archived: boolean;
  segment?: string | null;
  /**
   * Ids of the OTHER container types this one is physically interchangeable with —
   * the shared-lid case (the M and L cup use the same lid and the QR code sits on
   * the lid, so a scan cannot tell them apart). Never contains this type's own id.
   * A picking scan of any member satisfies an ordered bucket of any other member.
   */
  containerEquivalentTypeIds?: number[];
}

interface IContainer {
  id: string;
  name: string;
  shortId?: string | null;
  type: ContainerType;
  externalId?: string | null;
  cohort?: string | null;
}

export interface ContainerPageProps {
  containerId: string;
}

const ContainerPage: React.FC<ContainerPageProps> = ({ containerId }) => {
  const [locations, setLocations] = useState<ILocation[]>([]);
  const [container, setContainer] = useState<IContainer | null>(null);
  const [hasErrorLoading, setHasErrorLoading] = useState(false);

  useDocumentTitle(
    'PageTitles:container',
    container?.name || container?.shortId || containerId,
  );
  const [newDeadline, setNewDeadline] = useState<Date | null>(null);
  const [checkoutCount, setCheckoutCount] = useState(0);
  const [internalTouchPointCount, setInternalTouchPointCount] = useState(0);
  const [daysInSystem, setDaysInSystem] = useState(0);
  const [selectedStatus, setSelectedStatus] =
    useState<ContainerLocationStatus | null>(null);
  const [soldCount, setSoldCount] = useState(0);
  const [deadlineExtensionsCount, setDeadlineExtensionsCount] = useState(0);
  const [containerStations, setContainerStations] = useState<IdName[]>([]);
  const [jumpToDate, setJumpToDate] = useState<string | null>(null);

  useEffect(() => {
    if (containerId) {
      reloadContainer(containerId);
    }
  }, [containerId]);

  const extendReturnDeadlineBy7Days = (location: ILocation) => {
    if (!window.confirm(i18n.t('Containers:confirmExtendDeadline'))) {
      return;
    }
    if (location !== undefined && location.returnDeadline !== null) {
      let deadline: Date = new Date(location.returnDeadline);
      deadline = DateTimeHelpers.addDays(deadline, 7);
      location.returnDeadline =
        DateTimeHelpers.toDateTimeOffsetString(deadline);
      updateReturnDeadline(location);
    } else {
      console.error(
        'Can not update location deadline because it is undefined.',
      );
    }
  };

  const setReturnDeadline = (locationId: number, date: Date | null) => {
    const location: ILocation | undefined = locations.find(
      (l) => l.id === locationId,
    );
    if (location !== undefined && date !== null) {
      location.returnDeadline = DateTimeHelpers.toDateTimeOffsetString(date);
      updateReturnDeadline(location);
    } else {
      console.error(
        'Can not find location with id: ' +
          locationId +
          ' or return deadline: ' +
          date +
          ' invalid.',
      );
    }
  };

  const updateReturnDeadline = (location: ILocation) => {
    if (location.returnDeadline !== null && container !== null) {
      Communicator.PostData('/Container/UpdateContainerDeadline', {
        locationId: location.id,
        returnDeadline: location.returnDeadline,
      }).then(() => {
        if (container !== null) {
          reloadContainer(container.id);
        }
      });
    } else {
      console.error(
        'Can not update return deadline. Invalid return deadline or container.',
      );
    }
  };

  // const updateOnHoldState = (location: ILocation, isOnHold: boolean) => {
  //   Communicator.PostData('/Container/UpdateContainerLocationOnHoldState', {
  //     locationId: location.id,
  //     isOnHold: isOnHold,
  //   }).then((response) => {
  //     if (container != null) {
  //       reloadContainer(container.id);
  //     }
  //   });
  // };

  const changeContainerLocationStatus = (
    location: ILocation,
    status?: ContainerLocationStatus | null,
  ) => {
    if (status === null) {
      return;
    }
    Communicator.PostData('/Container/ChangeContainerLocationStatus', {
      containerLocationIds: [location.id],
      status: status,
    }).then(() => {
      if (container !== null) {
        reloadContainer(container.id);
      }
    });
  };

  const reloadContainer = (containerId: string) => {
    setHasErrorLoading(false);
    setNewDeadline(null);
    Communicator.Get('/Container/GetContainerHistory', {
      containerId: containerId,
    })
      .then((response) => response.json())
      .then((response: HistoryResponse) => {
        var usersById: { [id: string]: IUser } = {};
        response.users.forEach((u) => (usersById[u.id] = u));

        response.merchants.forEach((m) => {
          m.managerIds.forEach((mi) => (usersById[mi].merchant = m));

          m.stores.forEach((ms) => {
            ms.managerIds.concat(ms.appUserIds).forEach((mi) => {
              var user = usersById[mi];
              user.merchant = m;
              user.merchantStore = ms;
            });
          });
        });

        var locationsById: { [id: string]: ILocation } = {};
        response.locations.forEach((l) => {
          l.owner = usersById[l.ownerId];
          locationsById[l.id] = l;
        });
        setLocations(response.locations);
        setContainer(response.container);
        setCheckoutCount(response.checkoutCount);
        setInternalTouchPointCount(response.internalTouchPointCount);
        setDaysInSystem(response.daysInSystem);
        setSoldCount(response.soldCount);
        setDeadlineExtensionsCount(response.deadlineExtensionsCount);
        setContainerStations(response.containerStations);
      })
      .catch(() => {
        setHasErrorLoading(true);
      });
  };

  const toggleIsBillable = (location: ILocation) => {
    Communicator.PostData('/Container/UpdateContainerLocationIsBillable', {
      locationId: location.id,
      isBillable: !location.isBillableTransaction,
    }).then(() => {
      if (container !== null) {
        reloadContainer(container.id);
      }
    });
  };

  const uniqueDatesFromLocations = useMemo(() => {
    const dates = locations.map((location) => {
      const date = new Date(location.ownerSince);
      return date.toDateString();
    });

    // Remove duplicates and sort in descending order (newest first)
    const uniqueDates = Array.from(new Set(dates));
    return uniqueDates.sort(
      (a, b) => new Date(b).getTime() - new Date(a).getTime(),
    );
  }, [locations]);

  const jumpToLocationByDate = (selectedDate: string) => {
    if (!selectedDate) return;

    // Find the location with the matching date
    const targetLocation = locations.find((location) => {
      const locationDate = new Date(location.ownerSince);
      return locationDate.toDateString() === selectedDate;
    });

    if (targetLocation) {
      // Scroll to the timeline entry
      const element = document.getElementById(`location-${targetLocation.id}`);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        // Highlight the element briefly
        element.style.backgroundColor = '#fff3cd';
        setTimeout(() => {
          element.style.backgroundColor = '';
        }, 1500);
      }
    }
  };

  return (
    <PageContainer>
      <ContentSection
        title={i18n.t('Containers:containerHistoryFor', {
          containerName: container?.name,
        })}
        collapsable={false}
      >
        <PageHeader
          title={i18n.t('Containers:containerHistoryFor', {
            containerName: container?.name,
          })}
          subtitle={i18n.t('Containers:idLabel', {
            id: container?.id,
          })}
          secondSubtitle={i18n.t('Containers:shortIdLabel', {
            shortId: container?.shortId,
          })}
          qrCode={
            <>
              <img
                alt={i18n.t('Containers:qrCodeAlt')}
                src={getContainerQrCodeImgUrl(
                  container?.id,
                  container?.shortId,
                )}
              />
              <div className="mt-2">
                <ContainerTypeDisplay
                  containerType={container?.type}
                  showContainerId={false}
                  showName={false}
                  size="xl"
                />
              </div>
            </>
          }
        >
          <h4>
            {i18n.t('Containers:externalIdLabel', {
              externalId: container?.externalId || '-',
            })}
          </h4>
          <h4>
            {i18n.t('Containers:cohortLabel', {
              cohort: container?.cohort || '-',
            })}
          </h4>
          <div className="flex-row">
            <h4 style={{ float: 'left' }}>{i18n.t('Containers:typeLabel')}</h4>
            <h4>&nbsp;{container?.type.name}</h4>
          </div>
          <h4 style={{ clear: 'both' }}>
            {i18n.t('Containers:checkoutCountLabel', {
              count: checkoutCount,
            })}
          </h4>
          <h4>
            {i18n.t('Containers:internalTouchPointsLabel', {
              count: internalTouchPointCount,
            })}
          </h4>
          <h4>
            {i18n.t('Containers:daysInSystemLabel', {
              days: daysInSystem,
            })}
          </h4>
          <h4>
            {i18n.t('Containers:extensionCountLabel', {
              count: deadlineExtensionsCount,
            })}
          </h4>
          <h4>
            {i18n.t('Containers:soldCountLabel', {
              count: soldCount,
            })}
          </h4>
          <button
            className="form-control float-end"
            disabled={container == null}
          >
            <Link
              to={`${Routes.containers()}?isCurrentLocation=true&name=${
                container?.name
              }&exactMatch=true&searchName=true`}
            >
              {i18n.t('Containers:goToContainerSearch')}
            </Link>
          </button>
        </PageHeader>
        {!container && (
          <LoadingContainer
            hasErrorLoading={hasErrorLoading}
            errorMessage={i18n.t('Containers:errorLoading', {
              containerId: containerId,
            })}
          />
        )}
        {container && (
          <ContainerBrokenReports
            containerId={container.id}
            onCleared={() => reloadContainer(container.id)}
          />
        )}
        {container && (
          <div id="timeline">
            {locations.length > 1 && (
              <div className="mb-4">
                <div className="input-group" style={{ maxWidth: '400px' }}>
                  <span className="input-group-text">
                    {i18n.t('Containers:jumpToDate')}
                  </span>
                  <select
                    className="form-control"
                    value={jumpToDate || ''}
                    onChange={(e) => {
                      const selectedDate = e.target.value;
                      setJumpToDate(selectedDate);
                      jumpToLocationByDate(selectedDate);
                    }}
                  >
                    <option value="" disabled>
                      {i18n.t('Containers:selectDate')}
                    </option>
                    {uniqueDatesFromLocations.map((date) => (
                      <option key={date} value={date}>
                        {date}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {locations.map((location, idx) => (
              <ContainerLocationItem
                key={`location-${location.id}`}
                location={location}
                index={idx}
                nextLocationNumber={
                  idx > 0 ? locations[idx - 1].locationNumber : null
                }
                newDeadline={newDeadline}
                containerStations={containerStations}
                selectedStatus={selectedStatus}
                onExtendDeadline={extendReturnDeadlineBy7Days}
                onSetDeadline={setReturnDeadline}
                onSetNewDeadline={setNewDeadline}
                onToggleIsBillable={toggleIsBillable}
                onChangeStatus={changeContainerLocationStatus}
                onStatusChange={setSelectedStatus}
              />
            ))}
          </div>
        )}
      </ContentSection>
    </PageContainer>
  );
};

export default ContainerPage;
